import { useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Shirt, Star, Type, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import type { DirectSaleRailAction } from './DirectSaleDesktopRail';

/** The dialog offers the same ways in as the rail. */
export type DirectSaleStartChoice = DirectSaleRailAction;

/** One way in: the icon above its name, the whole tile is the button. */
function StartTile({
  icon,
  className,
  onClick,
  children,
}: {
  icon: ReactNode;
  className?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-card bg-paper-2 px-3 text-[13px] font-bold text-ink transition-colors hover:bg-paper-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary',
        className
      )}
    >
      <span className="text-primary" aria-hidden="true">
        {icon}
      </span>
      {children}
    </button>
  );
}

/**
 * What greets a customer on an empty product: pick another product, or
 * start with an upload, a text or a motif (R30).
 *
 * The dialog only reports the choice; the design step opens the matching
 * tool and remembers per product that it was shown.
 *
 * A tile closes the dialog first and reports the choice once it is gone:
 * while it is open its focus trap pulls the focus back from whatever the
 * tool focuses — a text in edit mode would lose the keyboard at once.
 * (Desktop only; the phone, where the keyboard has to open within the tap,
 * has its own modal.)
 */
export function DirectSaleStartDialog({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  /** The dialog is to close: without a choice, or on the way to one. */
  onClose: () => void;
  /** After the dialog has closed and let go of the focus. */
  onPick: (choice: DirectSaleStartChoice) => void;
}) {
  const { t } = useTranslation();
  const icon = 'h-7 w-7';
  const pickedRef = useRef<DirectSaleStartChoice | null>(null);
  const pick = (choice: DirectSaleStartChoice) => {
    pickedRef.current = choice;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        closeLabel={t('directSale.design.close')}
        // The tiles are the content; there is nothing to describe.
        aria-describedby={undefined}
        // First paper, so that the tiles in the second one stand out.
        className="max-w-[500px] bg-paper"
        onCloseAutoFocus={(event) => {
          const picked = pickedRef.current;
          pickedRef.current = null;
          if (picked === null) return;
          // The tool takes the focus itself; handing it back to where it
          // was before the dialog would take it away again.
          event.preventDefault();
          onPick(picked);
        }}
      >
        <DialogTitle className="font-display text-2xl font-bold text-primary">
          {t('directSale.mobile.startDesigning')}
        </DialogTitle>

        <div className="flex flex-col gap-3">
          <StartTile
            icon={<Shirt className="h-9 w-9" />}
            className="h-[124px]"
            onClick={() => pick('products')}
          >
            {t('directSale.mobile.products')}
          </StartTile>

          <p className="text-center text-sm text-mute">
            {t('directSale.desktop.startOr')}
          </p>

          <div className="grid grid-cols-3 gap-3">
            <StartTile
              icon={<Upload className={icon} />}
              className="h-24"
              onClick={() => pick('upload')}
            >
              {t('directSale.desktop.uploads')}
            </StartTile>
            <StartTile
              icon={<Type className={icon} />}
              className="h-24"
              onClick={() => pick('text')}
            >
              {t('directSale.mobile.textShort')}
            </StartTile>
            <StartTile
              icon={<Star className={icon} />}
              className="h-24"
              onClick={() => pick('motifs')}
            >
              {t('directSale.design.motifs')}
            </StartTile>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
